import {BulkCodonOptimizationOptions} from 'molbio/codon-optimization/BulkCodonOptimizationModal';
import {
  CodonOptimizationMethod,
  GCContent,
  GCLevel,
  HairpinParameter,
  OptimizeCodonsResult,
  Organism,
} from 'molbio/codon-optimization/CodonOptimizationConstants';
import {getApiPatternsToReduce} from 'molbio/codon-optimization/CodonOptimizationUtil';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {WorklistSummary} from 'worklists/WorklistConstants';

export interface APIProtectedRegion {
  start: number;
  end: number;
}

export interface APIEnzymeRecognitionSite {
  start: number;
  end: number;
  recognitionSiteDna: string;
}

function getGCApiParams(
  gcLevel: GCLevel,
  gcContent: GCContent
): {
  gcLevel?: GCLevel;
  gcContent?: GCContent;
} {
  return {
    gcLevel: gcLevel === GCLevel.CUSTOM ? undefined : gcLevel,
    gcContent: gcLevel === GCLevel.CUSTOM ? gcContent : undefined,
  };
}

export function optimizeCodons(
  bases: string,
  organism: Organism | null,
  gcLevel: GCLevel,
  protectedRegions: Array<APIProtectedRegion> = [],
  cutsitesToAvoid: Array<APIEnzymeRecognitionSite> = [],
  minimizeThymine: boolean = false,
  avoidHairpins: boolean = false,
  hairpinStem: HairpinParameter = {value: 20, error: ''},
  hairpinWindow: HairpinParameter = {value: 200, error: ''},
  patternsToMinimize: Array<string> = [],
  method: CodonOptimizationMethod,
  gcContent: GCContent
): Promise<OptimizeCodonsResult> {
  return net.postJSONPromise({
    url: '/1/api/optimize-codons',
    data: {
      bases,
      organism,
      protectedRegions,
      cutsitesToAvoid,
      minimizeThymine,
      hairpinParams: avoidHairpins ? {stem: hairpinStem.value, window: hairpinWindow.value} : null,
      patternsToMinimize,
      method,
      ...getGCApiParams(gcLevel, gcContent),
    },
  });
}

export interface BulkCodonOptimizationSequencesValidationResult {
  errors: Array<string>;
  valid: boolean;
}

export function validateSequencesForBulkCodonOptimization(
  sequenceIds: Array<string>
): Promise<BulkCodonOptimizationSequencesValidationResult> {
  return net.postJSONPromise({
    url: '/1/api/validate-sequences-for-bulk-codon-optimization',
    data: {
      sequenceIds,
    },
  });
}

export async function bulkBackTranslateOrCodonOptimize(
  entityIds: Array<string>,
  parameters: BulkCodonOptimizationOptions,
  isBackTranslation: boolean
): Promise<{worklistSummary?: WorklistSummary}> {
  const data = {
    folderId: parameters.sequencesFolderId,
    organism: parameters.selectedOrganism,
    cutsitesToAvoid: parameters.cutsitesToAvoid,
    minimizeThymine: parameters.uridineDepletion,
    hairpinParams: parameters.avoidHairpins
      ? {stem: parameters.hairpinStem.value, window: parameters.hairpinWindow.value}
      : null,
    existingWorklistId: parameters.existingWorklistId,
    newWorklistName: parameters.newWorklistName,
    tagSchemaId: parameters.tagSchema ? parameters.tagSchema.id : null,
    patternsToMinimize: getApiPatternsToReduce(parameters.patternsToReduce),
    method: parameters.method,
    ...getGCApiParams(parameters.selectedGCLevel, parameters.gcContent),
  };
  return await TaskRunner.postJSONPromise({
    url: isBackTranslation ? '/1/api/bulk-back-translate' : '/1/api/bulk-codon-optimize',
    data: isBackTranslation
      ? {
          ...data,
          proteinIds: entityIds,
        }
      : {
          ...data,
          sequenceIds: entityIds,
        },
  });
}
